import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { parentAPI } from '../../services/api';
import { notificationService } from '../../services/notificationService';
import { assignmentService } from '../../services/assignmentService';
import { examService } from '../../services/examService';
import { attendanceService } from '../../services/attendanceService';
import { gradeService } from '../../services/gradeService';
import { userService } from '../../services/userService';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  Award,
  AlertCircle,
  TrendingUp,
  UserPlus,
  Download,
  BarChart3,
  MessageCircle,
  FileText
} from 'lucide-react';

const ParentDashboard = ({ user }) => {
  const [children, setChildren] = React.useState([]);
  const [selectedChild, setSelectedChild] = React.useState(null);
  const [recentActivities, setRecentActivities] = React.useState([]);
  const [upcomingEvents, setUpcomingEvents] = React.useState([]);
  const [academicProgress, setAcademicProgress] = React.useState([]);
  const [consolidatedData, setConsolidatedData] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [activeView, setActiveView] = React.useState('overview');

  React.useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const kids = await userService.getChildren(user.id);
        const childrenData = kids.data.data || [];
        setChildren(childrenData);
        
        // Set first child as selected by default
        if (childrenData.length > 0 && !selectedChild) {
          setSelectedChild(childrenData[0]);
        }

        // Fetch consolidated data for all children
        const consolidated = await fetchConsolidatedData(childrenData);
        setConsolidatedData(consolidated);

        // Use notifications as recent activities surrogate
        const notes = await notificationService.getNotifications({ limit: 10 });
        const mappedNotes = (notes.data.data || []).map((n, idx) => ({
          id: n.id || idx,
          child: n.meta?.childName || '-',
          activity: n.title || n.message,
          time: new Date(n.createdAt).toLocaleString(),
          type: n.type || 'notice'
        }));
        setRecentActivities(mappedNotes);

        // Events placeholder from notifications of type 'event'
        const events = (notes.data.data || []).filter(n => n.type === 'event').slice(0,5).map((n, i) => ({
          id: n.id || i,
          title: n.title || 'School Event',
          date: new Date(n.createdAt).toLocaleDateString(),
          time: new Date(n.createdAt).toLocaleTimeString()
        }));
        setUpcomingEvents(events);

        // Academic progress for selected child or all children
        const progress = await fetchAcademicProgress(selectedChild || childrenData[0]);
        setAcademicProgress(progress);

      } catch (e) {
        console.error('Error fetching parent dashboard data:', e);
        setChildren([]);
        setRecentActivities([]);
        setUpcomingEvents([]);
        setAcademicProgress([]);
        setConsolidatedData({});
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [selectedChild]);

  // Fetch consolidated data for all children
  const fetchConsolidatedData = async (childrenData) => {
    try {
      const consolidated = {
        totalChildren: childrenData.length,
        averageAttendance: 0,
        totalAssignments: 0,
        upcomingExams: 0,
        recentGrades: [],
        attendanceSummary: {},
        performanceSummary: {}
      };

      // Calculate consolidated metrics from actual data
      for (const child of childrenData) {
        // Fetch assignments for this child
        try {
          const assignmentsResponse = await assignmentService.getAssignments({ studentId: child.id });
          consolidated.totalAssignments += assignmentsResponse.data?.data?.length || 0;
        } catch (error) {
          console.error('Error fetching assignments:', error);
        }

        // Fetch upcoming exams for this child
        try {
          const examsResponse = await examService.getExams({ studentId: child.id, upcoming: true });
          consolidated.upcomingExams += examsResponse.data?.data?.length || 0;
        } catch (error) {
          console.error('Error fetching exams:', error);
        }

        // Fetch attendance for this child
        try {
          const attendanceResponse = await attendanceService.getAttendance({ studentId: child.id });
          const attendanceData = attendanceResponse.data?.data || [];
          if (attendanceData.length > 0) {
            const presentDays = attendanceData.filter(a => a.status === 'present').length;
            consolidated.averageAttendance += (presentDays / attendanceData.length) * 100;
          }
        } catch (error) {
          console.error('Error fetching attendance:', error);
        }

        // Fetch recent grades for this child
        try {
          const gradesResponse = await gradeService.getGrades({ studentId: child.id, limit: 3 });
          const grades = gradesResponse.data?.data || [];
          grades.forEach(grade => {
            consolidated.recentGrades.push({
              child: child.firstName,
              subject: grade.subject?.name || 'Unknown',
              grade: grade.grade || 'N/A',
              score: grade.score || 0
            });
          });
        } catch (error) {
          console.error('Error fetching grades:', error);
        }
      }

      // Calculate average attendance
      if (childrenData.length > 0) {
        consolidated.averageAttendance = Math.round(consolidated.averageAttendance / childrenData.length);
      }

      return consolidated;
    } catch (error) {
      console.error('Error fetching consolidated data:', error);
      return {};
    }
  };

  // Fetch academic progress for a specific child
  const fetchAcademicProgress = async (child) => {
    if (!child) return [];
    
    try {
      // Fetch academic progress from multiple sources
      const [assignmentsRes, examsRes, gradesRes] = await Promise.all([
        assignmentService.getAssignments({ studentId: child.id }),
        examService.getExams({ studentId: child.id }),
        gradeService.getGrades({ studentId: child.id })
      ]);

      const assignments = assignmentsRes.data?.data || [];
      const exams = examsRes.data?.data || [];
      const grades = gradesRes.data?.data || [];

      // Calculate progress metrics
      const completedAssignments = assignments.filter(a => a.status === 'submitted').length;
      const upcomingExams = exams.filter(e => new Date(e.examDate) > new Date()).length;
      const averageGrade = grades.length > 0 
        ? grades.reduce((sum, g) => sum + (g.score || 0), 0) / grades.length 
        : 0;

      return {
        completedAssignments,
        totalAssignments: assignments.length,
        upcomingExams,
        averageGrade: Math.round(averageGrade),
        recentGrades: grades.slice(0, 5)
      };
    } catch (error) {
      console.error('Error fetching academic progress:', error);
      return {
        completedAssignments: 0,
        totalAssignments: 0,
        upcomingExams: 0,
        averageGrade: 0,
        recentGrades: []
      };
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Parent Dashboard
              </h1>
              <p className="text-gray-600">
                Welcome, {user?.firstName || 'Parent'}! Monitor your children's academic progress and school activities.
              </p>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Link Child
              </Button>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download Reports
              </Button>
            </div>
          </div>
        </div>

        {/* Child Selector */}
        {children.length > 1 && (
          <div className="mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700">View:</span>
                  <div className="flex space-x-2">
                    {children.map((child) => (
                      <Button
                        key={child.id}
                        variant={selectedChild?.id === child.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedChild(child)}
                      >
                        {child.firstName} {child.lastName}
                      </Button>
                    ))}
                    <Button
                      variant={activeView === 'overview' ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveView('overview')}
                    >
                      All Children
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs for different views */}
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="academic">Academic</TabsTrigger>
            <TabsTrigger value="activities">Activities</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Children</p>
                      <p className="text-2xl font-bold text-gray-900">{consolidatedData.totalChildren || children.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <BookOpen className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Assignments</p>
                      <p className="text-2xl font-bold text-gray-900">{consolidatedData.totalAssignments || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Calendar className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Upcoming Exams</p>
                      <p className="text-2xl font-bold text-gray-900">{consolidatedData.upcomingExams || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Award className="h-8 w-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Avg Attendance</p>
                      <p className="text-2xl font-bold text-gray-900">{consolidatedData.averageAttendance || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Children Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child) => (
                <Card key={child.id} className="cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Users className="h-8 w-8 text-blue-600" />
                        <div className="ml-4">
                          <p className="text-lg font-bold text-gray-900">{child.firstName} {child.lastName}</p>
                          <p className="text-sm text-gray-500">{child.grade || child.className || 'Student'}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedChild(child)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="academic" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Academic Progress */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Academic Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {academicProgress.map((progress) => (
                      <div key={progress.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">{progress.subject}</h4>
                          <p className="text-sm text-gray-600">{progress.child}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-100 text-green-800">{progress.grade}</Badge>
                          <div className="flex items-center mt-1">
                            <TrendingUp className={`h-4 w-4 ${progress.trend === 'up' ? 'text-green-600' : 'text-red-600'}`} />
                            <span className="text-xs text-gray-500 ml-1">{progress.trend}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Grades */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Recent Grades
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {consolidatedData.recentGrades?.map((grade, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">{grade.subject}</h4>
                          <p className="text-sm text-gray-600">{grade.child}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-blue-100 text-blue-800">{grade.grade}</Badge>
                          <p className="text-sm text-gray-500">{grade.score}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="activities" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activities */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Recent Activities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3 p-4 border rounded-lg">
                        <div className="flex-shrink-0">
                          {activity.type === 'assignment' && <BookOpen className="h-5 w-5 text-blue-600" />}
                          {activity.type === 'grade' && <Award className="h-5 w-5 text-green-600" />}
                          {activity.type === 'exam' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
                          {activity.type === 'notice' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.activity}</p>
                          <p className="text-xs text-gray-600">{activity.child} • {activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Upcoming Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {upcomingEvents.map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                          <p className="text-sm text-gray-600">{event.date} at {event.time}</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">Upcoming</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Report Cards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Download All Report Cards
                    </Button>
                    <Button className="w-full" variant="outline">
                      <FileText className="w-4 h-4 mr-2" />
                      View Academic Summary
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Performance Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button className="w-full" variant="outline">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      View Performance Trends
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Award className="w-4 h-4 mr-2" />
                      Compare Children
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Communication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button className="w-full" variant="outline">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message Teachers
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Meeting
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
  );
};

export default ParentDashboard;
